import { ReactNode, createContext, useContext, useState } from 'react'
import { cn } from '@/lib/cn'

interface TabsContextValue {
  activeTab: string
  setActiveTab: (value: string) => void
}

const TabsContext = createContext<TabsContextValue | undefined>(undefined)

function useTabsContext() {
  const context = useContext(TabsContext)
  if (!context) {
    throw new Error('Tabs components must be used within a Tabs provider')
  }
  return context
}

export interface TabsProps {
  children: ReactNode
  className?: string
  // Uncontrolled mode
  defaultValue?: string
  // Controlled mode
  value?: string
  onValueChange?: (value: string) => void
}

export function Tabs({ children, className, defaultValue, value, onValueChange }: TabsProps) {
  // Internal state for uncontrolled mode
  const [internalTab, setInternalTab] = useState(defaultValue || value || '')

  // Determine if controlled or uncontrolled
  const isControlled = value !== undefined && onValueChange !== undefined
  const activeTab = isControlled ? value : internalTab
  const setActiveTab = isControlled ? onValueChange : setInternalTab

  return (
    <TabsContext.Provider value={{ activeTab, setActiveTab }}>
      <div className={className}>{children}</div>
    </TabsContext.Provider>
  )
}

export interface TabsListProps {
  children: ReactNode
  className?: string
}

export function TabsList({ children, className }: TabsListProps) {
  return (
    <div
      className={cn(
        'inline-flex items-center justify-center rounded-lg bg-muted p-1',
        className
      )}
    >
      {children}
    </div>
  )
}

export interface TabsTriggerProps {
  value: string
  children: ReactNode
  className?: string
}

export function TabsTrigger({ value, children, className }: TabsTriggerProps) {
  const { activeTab, setActiveTab } = useTabsContext()
  const isActive = activeTab === value

  return (
    <button
      onClick={() => setActiveTab(value)}
      className={cn(
        'inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1.5',
        'text-sm font-medium transition-all',
        'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background',
        isActive
          ? 'bg-card text-foreground shadow-elevation-1'
          : 'text-muted-foreground hover:text-foreground',
        className
      )}
    >
      {children}
    </button>
  )
}

export interface TabsContentProps {
  value: string
  children: ReactNode
  className?: string
}

export function TabsContent({ value, children, className }: TabsContentProps) {
  const { activeTab } = useTabsContext()

  if (activeTab !== value) {
    return null
  }

  return <div className={className}>{children}</div>
}
